import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { isValidPhoneNumber as isValidNumber } from 'libphonenumber-js/min'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { parseCookies, setCookie } from 'nookies'
import { useSnackbar } from 'notistack'
import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { SpaceType } from 'src/components/SearchBar/types'
import { STORAGE_TIME_MAP } from 'src/constants'
import { useAuthProvider } from 'src/context/AuthProvider'
import { useFilterProvider } from 'src/context/FilterProvider'
import useErrorSnackbar from 'src/hooks/useErrorSnackbar'
import useWhatsappCityHint from 'src/hooks/useWhatsappCityHint'
import {
  CustomerType,
  IMutation,
  IMutationSendCustomerInfoEmailArgs,
  Interest,
  LocationType,
  StorageTime
} from 'src/types/graphql'
import { RedirectUrls } from 'src/types/types'
import { isValidEmail } from 'src/utils/email'
import { identifyUser, useGenerateLead } from 'src/utils/pixels/common'
import ga from 'src/utils/pixels/gtag'
import posthog from 'src/utils/pixels/posthog'
import { appendCityHint, buildWhatsappUrl } from 'src/utils/whatsapp'
import BaseButton from '../Buttons/BaseButton'
import BaseSelect from '../Inputs/BaseSelect'
import BaseTextField from '../Inputs/BaseTextField'
import PhoneField from '../Inputs/PhoneField'
import { NearbySearchCountsResult, useNearbySearchCounts } from './hostCtaQuery'

// eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
const PLACE_TYPE_KEYS = {
  na: 'FORMS.NEED',
  estacionamiento: 'LOCATION_TYPE.PARKING',
  mini: 'LOCATION_TYPE.SELF_STORAGE_UNIT',
  bodega: 'LOCATION_TYPE.BODEGA_COMMERCIAL',
  bodega_industrial: 'LOCATION_TYPE.BODEGA_INDUSTRIAL',
  nave_industrial: 'LOCATION_TYPE.NAVE_INDUSTRIAL'
} as const

const PLACE_TYPE_TO_LOCATION_TYPE: Record<string, LocationType> = {
  estacionamiento: LocationType.Parking,
  mini: LocationType.SelfStorageUnit,
  bodega: LocationType.BodegaCommercial,
  bodega_industrial: LocationType.BodegaIndustrial,
  nave_industrial: LocationType.NaveIndustrial
}

type PlaceTypeKey = keyof typeof PLACE_TYPE_KEYS

const SPACE_TYPE_TO_PLACE_TYPE: Partial<Record<SpaceType, PlaceTypeKey>> = {
  [SpaceType.PARKING]: 'estacionamiento',
  [SpaceType.SELF_STORAGE]: 'mini',
  [SpaceType.WAREHOUSE]: 'bodega',
  [SpaceType.INDUSTRIAL_WAREHOUSE]: 'nave_industrial'
}

const SEND_CUSTOMER_INFO_EMAIL = gql`
  mutation SendCustomerInfoEmail(
    $customerInfo: CustomerInfoInput!
    $searchInput: SearchLocationsInput!
  ) {
    sendCustomerInfoEmail(
      customerInfo: $customerInfo
      searchInput: $searchInput
    )
  }
`

type IForm = {
  name: string
  email: string
  phone: string
  placeType: PlaceTypeKey
  storageTime: StorageTime | 'na'
  quantity: number
  additionalInfo: string | null
}

type IProps = {
  locationName?: string
  /** True when the search returned ZERO results (vs the sparse 1-7 path).
   *  Switches the email field to optional and trims the form to the
   *  waitlist essentials. */
  zeroResults?: boolean
  /** SSG nearby-supply counts when the page has them (pillar/city/hood
   *  pages pass them down from getStaticProps; /search self-queries). */
  nearbySearchData?: NearbySearchCountsResult
}

function CaptureLead({ locationName, zeroResults, nearbySearchData }: IProps) {
  const { t: tCommon } = useTranslation('common')
  const { enqueueSnackbar } = useSnackbar()
  const enqueueError = useErrorSnackbar()

  const PLACE_TYPE_MAP: Record<PlaceTypeKey, string> = {
    na: tCommon('FORMS.NEED'),
    estacionamiento: tCommon('LOCATION_TYPE.PARKING'),
    mini: tCommon('LOCATION_TYPE.SELF_STORAGE_UNIT'),
    bodega: tCommon('LOCATION_TYPE.BODEGA_COMMERCIAL'),
    bodega_industrial: tCommon('LOCATION_TYPE.BODEGA_INDUSTRIAL'),
    nave_industrial: tCommon('LOCATION_TYPE.NAVE_INDUSTRIAL')
  }
  const { currentUser } = useAuthProvider()
  const { filters } = useFilterProvider()
  // Fires GA4/Meta/Bing/LinkedIn/PostHog pixel AND writes LEAD_SUBMIT
  // (subType='search_empty') to ES so marketing-report attributes
  // empty-search-recovery leads.
  const generateLead = useGenerateLead()

  // Availability-honest framing (FUNNELS.md F1 Phase 1). Only when the
  // search returned zero results AND the nearby-supply probe confirms there
  // is genuinely nothing around does the card switch to the waitlist frame
  // ("aún no tenemos espacios aquí — te avisamos"). A zero-result search in
  // a supplied market (over-filtering, wrong pin) keeps the generic "te
  // ayudamos a ampliar la búsqueda" frame — claiming "no spaces here" there
  // would be false. `undefined` (probe loading / no coords) = unknown →
  // generic frame. Shares HostCTA's query — Apollo dedupes in flight.
  const nearbySupply = useNearbySearchCounts(nearbySearchData)
  const isUnservedArea = !!zeroResults && nearbySupply?.total === 0

  // PostHog-only mid-funnel signal (same tier as `add_post_step` /
  // `kyc_step` — never a Google Ads event). Closes the F1 measurement gap:
  // zero-result sessions → capture_lead_start (form engagement) →
  // generate_lead method='search_empty' (submit). Once per mount via ref.
  const leadStartFiredRef = useRef(false)
  const fireLeadStart = () => {
    if (leadStartFiredRef.current) return
    leadStartFiredRef.current = true
    posthog.event('capture_lead_start', {
      zero_results: !!zeroResults,
      unserved_area: isUnservedArea,
      location_name: locationName ?? null
    })
  }

  // WhatsApp escape hatch — the second, no-form path out of a dead-end search.
  // The corporate-number prefill carries the approximate-city hint (`… · mty`)
  // so the LangGraph agent opens the conversation with the metro in mind. The
  // hook returns '' at SSR / before geo resolves, so the href matches server
  // and first client paint (no #418), then updates post-signal — same shape as
  // the floating FAB (`WhatsappButtonImpl`). Routes to SPOTME_WHATSAPP_NUMBER
  // (never a specialist's phone) — see the WhatsApp routing rule in CLAUDE.md.
  const cityHintCode = useWhatsappCityHint()
  const waHref = buildWhatsappUrl(
    appendCityHint(tCommon('CAPTURE_LEAD.WHATSAPP_MSG'), cityHintCode)
  )

  // NEW event, deliberately NOT `contact_wa` (that's listing-level intent).
  // `search_empty_wa_click` is part of the zero-results recovery definition —
  // fires via ga.event so it mirrors to GA4 + PostHog, where recovery insight
  // SihInJwm counts it among dead-end sessions. See FUNNELS.md F1 +
  // docs/marketing/zero-results-recovery.md.
  const fireWaClick = () => {
    ga.event('search_empty_wa_click', {
      zero_results: !!zeroResults,
      unserved_area: isUnservedArea,
      location_name: locationName ?? null,
      has_city_hint: !!cityHintCode
    })
  }

  const [existingLead, setExistingLead] = useState<Partial<IForm> | null>(null)
  const [sent, setSent] = useState(false)

  const { email, phoneNumber, firstname, userChanges } = currentUser ?? {}

  const knownPlaceType =
    filters.spaceTypes?.length === 1
      ? SPACE_TYPE_TO_PLACE_TYPE[filters.spaceTypes[0]]
      : undefined
  const initialPlaceType = knownPlaceType ?? 'na'

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
    setError,
    getValues,
    watch
  } = useForm<IForm>({
    defaultValues: {
      name: firstname ?? userChanges?.firstname ?? existingLead?.name,
      email: email ?? userChanges?.email ?? existingLead?.email,
      phone: phoneNumber ?? userChanges?.phoneNumber ?? existingLead?.phone,
      placeType: initialPlaceType,
      storageTime: 'na',
      quantity: 1,
      additionalInfo: existingLead?.additionalInfo ?? ''
    }
  })

  const selectedPlaceType = watch('placeType')

  const [sendCustomerInfoEmail, { loading }] = useMutation<
    Pick<IMutation, 'sendCustomerInfoEmail'>,
    IMutationSendCustomerInfoEmailArgs
  >(SEND_CUSTOMER_INFO_EMAIL, {
    onCompleted: () => {
      const vals = getValues()
      const locType = PLACE_TYPE_TO_LOCATION_TYPE[vals.placeType]

      generateLead({
        method: 'search_empty',
        locationType: locType,
        storageTime: vals.storageTime !== 'na' ? vals.storageTime : undefined,
        quantity: vals.quantity > 1 ? vals.quantity : undefined
      })

      enqueueSnackbar(tCommon('TOASTS.INFO_SENT'), { variant: 'success' })
      reset()
      setSent(true)

      if (!currentUser) return
      identifyUser(currentUser, null, vals)
    },
    onError: (error) => {
      enqueueError(error, { logMessage: 'Error creating lead' })
      setCookie(null, 'lead', '', { maxAge: 0 })
    }
  })

  const onSubmit = ({
    placeType,
    storageTime,
    quantity,
    additionalInfo,
    ...formValues
  }: IForm) => {
    if (!formValues.name?.trim()) {
      setError('name', { message: tCommon('FORMS.REQUIRED') })
      return
    }
    if (!placeType || placeType === 'na') {
      setError('placeType', { message: tCommon('FORMS.REQUIRED') })
      return
    }
    // Email is optional on the zero-results path (flotillas precedent:
    // phone is the identity, HubSpot dedupes by phone, and the server
    // converts '' → undefined before the CRM write so an empty value can
    // never clear an existing contact's email). Still validated when typed.
    if (formValues.email ? !isValidEmail(formValues.email) : !zeroResults) {
      setError('email', { message: tCommon('FORMS.INVALID_EMAIL') })
      return
    }
    if (!formValues.phone?.trim() || !isValidNumber(formValues.phone)) {
      setError('phone', { message: tCommon('FORMS.INVALID_PHONE') })
      return
    }
    const items = PLACE_TYPE_MAP[placeType]
    const {
      canStoreVehicle,
      sizes,
      minPrice,
      maxPrice,
      lat,
      lng,
      placeId,
      description
    } = filters
    const message = `Guardar: ${items}, Vehículo: ${canStoreVehicle ? 'Sí' : 'No'}, Tamaño: ${sizes?.join(', ') ?? 'na'}, Precio: ${minPrice ?? 'na'} - ${maxPrice ?? 'na'}, Cantidad: ${quantity}, Información adicional: ${additionalInfo}`
    sendCustomerInfoEmail({
      variables: {
        customerInfo: {
          ...formValues,
          // Schema requires String! — empty string when the optional email
          // was skipped; the resolver guards '' → undefined before HubSpot.
          email: formValues.email?.trim() ?? '',
          message,
          interest: Interest.Rent,
          customerType: CustomerType.RentIndividual,
          locationType: PLACE_TYPE_TO_LOCATION_TYPE[placeType],
          leadSource: 'search_empty_results',
          nearestCity: description ?? undefined,
          storageTime: storageTime !== 'na' ? storageTime : undefined,
          quantity: Number(quantity) > 1 ? Number(quantity) : undefined
        },
        searchInput: { placeId, center: lat && lng ? { lat, lng } : undefined }
      }
    })
    setCookie(null, 'lead', JSON.stringify(formValues), {
      maxAge: 30 * 24 * 60 * 60,
      path: '/'
    })
    setExistingLead(formValues)
  }

  useEffect(() => {
    const str = parseCookies(null).lead
    if (!str) return
    const lead = JSON.parse(str)
    if (lead) setExistingLead(lead)
  }, [reset])

  if (sent) {
    return (
      <div className="flex w-full justify-center">
        <div className="w-full rounded-2xl border border-ink-100/15 bg-white-500 p-6 text-center md:max-w-[600px] md:p-8">
          <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
            Solicitud recibida
          </p>
          <h3 className="mt-3 font-display text-xl leading-tight font-medium text-ink-700 md:text-2xl">
            Gracias, te contactamos pronto
          </h3>
          <p className="mt-2 text-[14px] leading-relaxed text-ink-200">
            Un asesor de SpotMe revisará tu búsqueda y te ayudará a encontrar el
            espacio ideal.
          </p>
        </div>
      </div>
    )
  }

  const headline = isUnservedArea ? (
    locationName ? (
      tCommon('CAPTURE_LEAD.UNSERVED_TITLE_CITY', { city: locationName })
    ) : (
      tCommon('CAPTURE_LEAD.UNSERVED_TITLE')
    )
  ) : locationName ? (
    <>
      {`¿No encuentras lo que buscas en `}
      <span className="font-display text-blue-500 italic">{locationName}</span>
      {'?'}
    </>
  ) : (
    <>
      {`¿No encuentras el espacio `}
      <span className="font-display text-blue-500 italic">perfecto</span>
      {'?'}
    </>
  )

  const subhead = isUnservedArea
    ? tCommon('CAPTURE_LEAD.UNSERVED_SUBHEAD')
    : 'Déjanos tus datos y un asesor de SpotMe te ayudará a encontrar el espacio ideal — ya sea ampliando la búsqueda, ajustando filtros o avisándote en cuanto se publique uno nuevo.'

  return (
    <form
      className="flex w-full justify-center"
      onSubmit={handleSubmit(onSubmit)}
      onFocusCapture={fireLeadStart}
    >
      {/* `@container`, not viewport `md:` — this form renders inside the 45%
          results rail, so at a 900px viewport its box is only ~357px and the
          `md:grid-cols-2` field grid split it into ~138px columns that
          truncated three of four placeholders ("Correo (opcion", "+52 55 1234
          5(", "¿Cuán…"). Every step below queries the card's own width. See
          `OnDemandRecoveryCard` for the full note. */}
      <div className="@container w-full rounded-2xl border border-ink-100/15 bg-white-500 p-5 text-ink-700 @md:p-8">
        <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
          Te ayudamos
        </p>
        <h3 className="mt-3 font-display text-xl leading-tight font-medium text-ink-700 md:text-[28px]">
          {headline}
        </h3>
        <p className="mt-2 text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
          {subhead}
        </p>
        <p className="mt-3 text-[13px] leading-relaxed text-ink-200">
          {'¿Prefieres seguir explorando primero? '}
          <Link
            href={RedirectUrls.Search}
            className="font-display text-blue-500 italic underline-offset-4 transition-colors hover:underline"
          >
            Ver espacios cercanos
          </Link>
          {'.'}
        </p>

        {/* WhatsApp escape hatch — sits above the form fields in BOTH the
            waitlist and broaden frames as a lower-friction alternative to the
            phone/email form. Green WhatsApp pill (bg-green-500) matches the
            listing PriceCard WA family; inline SVG icon (no next/image) keeps
            it hydration-safe. Full-width tap target in a narrow card, natural
            width once the card clears 448px. */}
        <div className="mt-5 flex flex-col gap-3 border-t border-ink-100/15 pt-5 @md:flex-row @md:items-center @md:justify-between">
          <p className="text-[13px] leading-relaxed text-ink-200">
            {tCommon('CAPTURE_LEAD.WHATSAPP_ALT')}
          </p>
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={fireWaClick}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-green-500 px-5 py-3 text-[13px] font-semibold whitespace-nowrap text-white-500 transition-colors hover:bg-green-600 @md:w-auto"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M11.61 9.58803C11.4113 9.48869 10.4373 9.01003 10.256 8.94336C10.0747 8.87736 9.94267 8.84469 9.81 9.04336C9.678 9.24069 9.29867 9.68736 9.18333 9.81936C9.06733 9.95203 8.952 9.96803 8.754 9.86936C8.556 9.76936 7.91733 9.56069 7.16067 8.88603C6.572 8.36069 6.174 7.71203 6.05867 7.51336C5.94333 7.31536 6.046 7.20803 6.14533 7.10936C6.23467 7.02069 6.34333 6.87803 6.44267 6.76269C6.542 6.64669 6.57467 6.56403 6.64067 6.43136C6.70733 6.29936 6.674 6.18403 6.624 6.08469C6.57467 5.98536 6.17867 5.01003 6.01333 4.61336C5.85267 4.22736 5.68933 4.28003 5.568 4.27336C5.452 4.26803 5.32 4.26669 5.188 4.26669C5.056 4.26669 4.84133 4.31603 4.66 4.51469C4.478 4.71269 3.96667 5.19203 3.96667 6.16736C3.96667 7.14203 4.676 8.08403 4.77533 8.21669C4.87467 8.34869 6.172 10.35 8.15933 11.208C8.63267 11.412 9.00133 11.534 9.28867 11.6247C9.76333 11.776 10.1953 11.7547 10.5367 11.7034C10.9167 11.6467 11.7087 11.224 11.874 10.7614C12.0387 10.2987 12.0387 9.90203 11.9893 9.81936C11.94 9.73669 11.808 9.68736 11.6093 9.58803H11.61ZM7.99533 14.5234H7.99267C6.81229 14.5236 5.65358 14.2063 4.638 13.6047L4.398 13.462L1.90333 14.1167L2.56933 11.6847L2.41267 11.4354C1.75276 10.3849 1.40353 9.16921 1.40533 7.92869C1.40667 4.29536 4.36267 1.33936 7.998 1.33936C9.758 1.33936 11.4127 2.02603 12.6567 3.27136C13.2703 3.88241 13.7566 4.60907 14.0875 5.40929C14.4185 6.20951 14.5874 7.06741 14.5847 7.93336C14.5833 11.5667 11.6273 14.5234 7.99533 14.5234ZM13.6033 2.32536C12.8688 1.58598 11.9948 0.999718 11.0321 0.600561C10.0693 0.201403 9.03689 -0.0027175 7.99467 2.73184e-05C3.62533 2.73184e-05 0.068 3.55669 0.0666667 7.92803C0.064642 9.31919 0.429583 10.6863 1.12467 11.8914L0 16L4.20267 14.8974C5.36525 15.5308 6.66806 15.8627 7.992 15.8627H7.99533C12.3647 15.8627 15.922 12.306 15.9233 7.93403C15.9266 6.89221 15.7232 5.86009 15.325 4.89738C14.9267 3.93467 14.3416 3.06047 13.6033 2.32536Z"
                fill="white"
              />
            </svg>
            {tCommon('CAPTURE_LEAD.WHATSAPP_CTA')}
          </a>
        </div>

        {/* Two columns only once the CARD clears 448px (~1100px viewport).
            Below that a single column beats two cramped ones with clipped
            placeholders — it's a form, legibility wins over density. */}
        <div className="mt-5 grid gap-3 @md:grid-cols-2 @md:gap-4">
          <div>
            <BaseTextField
              id="name"
              control={control}
              error={!!errors.name}
              placeholder={tCommon('FORMS.NAME_PLACEHOLDER')}
              required
              className="w-full"
              defaultValue={
                firstname ?? userChanges?.firstname ?? existingLead?.name
              }
            />
            {errors.name?.message ? (
              <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>
            ) : null}
          </div>
          <div>
            <BaseTextField
              id="email"
              control={control}
              error={!!errors.email}
              placeholder={
                zeroResults
                  ? tCommon('FORMS.EMAIL_PLACEHOLDER_OPTIONAL')
                  : tCommon('FORMS.EMAIL_PLACEHOLDER')
              }
              type="email"
              required={!zeroResults}
              className="w-full"
              defaultValue={email ?? userChanges?.email ?? existingLead?.email}
            />
            {errors.email?.message ? (
              <p className="mt-1 text-xs text-red-500">
                {errors.email.message}
              </p>
            ) : null}
          </div>
          <PhoneField<IForm>
            name="phone"
            control={control}
            rules={{ required: tCommon('FORMS.REQUIRED') }}
            required
            fullWidth
          />

          {!knownPlaceType && (
            <div>
              <BaseSelect
                id="placeType"
                control={control}
                error={!!errors.placeType}
                defaultValue="na"
                entries={PLACE_TYPE_MAP}
                displayEmpty
                rules={{ required: true }}
                fullWidth
              />
              {errors.placeType?.message ? (
                <p className="mt-1 text-xs text-red-500">
                  {errors.placeType.message}
                </p>
              ) : null}
            </div>
          )}
          <div>
            <BaseSelect
              id="storageTime"
              control={control}
              defaultValue="na"
              entries={{ na: tCommon('FORMS.HOW_LONG'), ...STORAGE_TIME_MAP }}
              displayEmpty
              fullWidth
            />
          </div>
          {selectedPlaceType === 'estacionamiento' && (
            <div>
              <BaseTextField
                id="quantity"
                control={control}
                placeholder={tCommon('FORMS.HOW_MANY_SPACES')}
                type="number"
                className="w-full"
                rules={{ min: 1, max: 100 }}
                defaultValue={1}
              />
            </div>
          )}
          {/* Waitlist mode trims to essentials — "tell us more" prose
              belongs to the broaden-the-search frame, not to "avísame". */}
          {!zeroResults && (
            <div className="md:col-span-2">
              <BaseTextField
                id="additionalInfo"
                control={control}
                error={!!errors.additionalInfo}
                placeholder={tCommon('FORMS.ADDITIONAL_INFO')}
                multiline
                rows={3}
                className="w-full"
                hideCounter
              />
            </div>
          )}
        </div>

        <div className="mt-5 flex flex-col gap-3 @md:flex-row @md:items-center @md:justify-between">
          <p className="text-xs leading-relaxed text-ink-200">
            {`Al enviar aceptas nuestra `}
            <Link href={RedirectUrls.Disclosures} className="text-blue-500">
              Política de Privacidad
            </Link>
            .
          </p>
          <BaseButton
            type="submit"
            rounded
            variant="contained"
            loading={loading}
            fullWidth
            className="@md:w-[180px]"
          >
            Enviar
          </BaseButton>
        </div>
      </div>
    </form>
  )
}

export default React.memo(CaptureLead)
